<template>
  <Modal :active="active" :title="$t('forms.offer.title')" :vehicle="vehicle" @modalClosed="closeModal" ref="modal">
    <div class="form-modal-intro">
      <div v-if="introText" v-html="introText"></div>
    </div>

    <p v-if="successMsg" class="form-success">{{ successMsg }}</p>
    <p v-if="errorMsg" class="form-error">{{ errorMsg }}</p>

    <form id="request-offer" @submit.prevent="submitForm($event)" @change="validateErrorFields">
      <p v-if="errors.length > 0" class="form-error">{{ $t('forms.errors.requiredFields') }}</p>

      <div class="formRow">
        <div class="formRow__label">
          <label class="form-label">{{ $t('forms.fields.salutation') }}</label>
        </div>
        <!-- .formRow__label -->
        <div class="formRow__field formRow__field--text">
          <div class="form-radio form-radio--inline">
            <input id="offer-salutation-1" class="form-radio__input" type="radio" value="Dhr." v-model="salutation" />
            <label class="form-radio__label" for="offer-salutation-1">{{ $t('forms.fields.salutationMale') }}</label>
          </div>
          <!-- .form-radio -->

          <div class="form-radio form-radio--inline">
            <input id="offer-salutation-2" class="form-radio__input" type="radio" value="Mevr." v-model="salutation" />
            <label class="form-radio__label" for="offer-salutation-2">{{ $t('forms.fields.salutationFemale') }}</label>
          </div>
          <!-- .form-radio -->

          <p v-if="isError('salutation')" class="form-fieldError">{{ $t('forms.errors.requiredFieldSingle') }}</p>
        </div>
        <!-- .formRow__field -->
      </div>
      <!-- .formRow -->

      <div class="formRow">
        <div class="formRow__label">
          <label class="form-label" for="lease-fname">{{ $t('forms.fields.name') }}</label>
        </div>
        <!-- .formRow__label -->
        <div class="formRow__field">
          <div class="formRow__field__grid">
            <div class="formRow__field__grid__column">
              <input
                id="lease-fname"
                class="form-textField"
                :class="{ 'form-textField--error': isError('fname') }"
                type="text"
                v-model="fname"
                :placeholder="$t('forms.fields.firstName')"
              />
              <p v-if="isError('fname')" class="form-fieldError">
                {{ $t('forms.errors.requiredFieldSingle') }}
              </p>
            </div>
            <!-- .formRow__field__grid__column -->
            <div class="formRow__field__grid__column">
              <input
                id="lease-lname"
                class="form-textField"
                :class="{ 'form-textField--error': isError('lname') }"
                type="text"
                v-model="lname"
                :placeholder="$t('forms.fields.lastName')"
              />
              <p v-if="isError('lname')" class="form-fieldError">
                {{ $t('forms.errors.requiredFieldSingle') }}
              </p>
            </div>
            <!-- .formRow__field__grid__column -->
          </div>
          <!-- .formRow__field__grid -->
        </div>
        <!-- .formRow__field -->
      </div>
      <!-- .formRow -->

      <div class="formRow">
        <div class="formRow__label">
          <label class="form-label" for="offer-email">{{ $t('forms.fields.email') }}</label>
        </div>
        <!-- .formRow__label -->
        <div class="formRow__field">
          <input
            id="offer-email"
            class="form-textField"
            :class="{ 'form-textField--error': isError('email') }"
            type="email"
            v-model="email"
          />
          <p v-if="isError('email')" class="form-fieldError">{{ $t('forms.errors.requiredFieldSingle') }}</p>
        </div>
        <!-- .formRow__field -->
      </div>
      <!-- .formRow -->

      <div class="formRow">
        <div class="formRow__label">
          <label class="form-label" for="offer-phoneNumber">{{ $t('forms.fields.phone') }}</label>
        </div>
        <!-- .formRow__label -->
        <div class="formRow__field">
          <input
            id="offer-phoneNumber"
            class="form-textField"
            :class="{ 'form-textField--error': isError('phoneNumber') }"
            type="text"
            v-model="phoneNumber"
          />
          <p v-if="isError('phoneNumber')" class="form-fieldError">{{ $t('forms.errors.requiredFieldSingle') }}</p>
        </div>
        <!-- .formRow__field -->
      </div>
      <!-- .formRow -->

      <div class="formRow">
        <div class="formRow__label">
          <label class="form-label" for="offer-fname">{{ $t('forms.offer.tradeInCar') }}</label>
        </div>
        <!-- .formRow__label -->
        <div class="formRow__field formRow__field--text">
          <div class="form-radio form-radio--inline">
            <input id="offer-tradeCar-true" class="form-radio__input" type="radio" :value="true" v-model="isTradeCar" />
            <label class="form-radio__label" for="offer-tradeCar-true">{{ $t('forms.fields.yes') }}</label>
          </div>
          <!-- .form-radio -->

          <div class="form-radio form-radio--inline">
            <input id="offer-tradeCar-false" class="form-radio__input" type="radio" :value="false" v-model="isTradeCar" />
            <label class="form-radio__label" for="offer-tradeCar-false">{{ $t('forms.fields.no') }}</label>
          </div>
          <!-- .form-radio -->

          <p v-if="isError('isTradeCar')" class="form-fieldError">{{ $t('forms.errors.requiredFieldSingle') }}</p>
        </div>
        <!-- .formRow__field -->
      </div>
      <!-- .formRow -->

      <div v-if="isTradeCar" class="formGroup formGroup--pushBottom">
        <div class="formRow">
          <div class="formRow__label">
            <label class="form-label" for="offer-license">{{ $t('forms.offer.yourLicense') }}</label>
          </div>
          <!-- .formRow__label -->
          <div class="formRow__field">
            <input
              id="offer-license"
              class="form-textField"
              :class="{ 'form-textField--error': isError('license') }"
              type="license"
              v-model="license"
            />
            <p v-if="isError('license')" class="form-fieldError">{{ $t('forms.errors.requiredFieldSingle') }}</p>
          </div>
          <!-- .formRow__field -->
        </div>
        <!-- .formRow -->

        <div class="formRow">
          <div class="formRow__label">
            <label class="form-label" for="offer-mileage">{{ $t('forms.fields.mileage') }}</label>
          </div>
          <!-- .formRow__label -->
          <div class="formRow__field">
            <input
              id="offer-mileage"
              class="form-textField"
              :class="{ 'form-textField--error': isError('mileage') }"
              type="text"
              v-model="mileage"
            />
            <p v-if="isError('mileage')" class="form-fieldError">{{ $t('forms.errors.requiredFieldSingle') }}</p>
          </div>
          <!-- .formRow__field -->
        </div>
        <!-- .formRow -->
      </div>
      <!-- .formGroup -->

      <div class="formRow">
        <div class="formRow__label">
          <label class="form-label" for="offer-comments">{{ $t('forms.fields.comments') }}</label>
        </div>
        <!-- .formRow__label -->
        <div class="formRow__field">
          <textarea
            id="offer-comments"
            class="form-textarea"
            :class="{ 'form-textField--error': isError('comments') }"
            v-model="comments"
          ></textarea>
          <p v-if="isError('comments')" class="form-fieldError">{{ $t('forms.errors.requiredFieldSingle') }}</p>
        </div>
        <!-- .formRow__field -->
      </div>
      <!-- .formRow -->

      <div class="formRow formRow--spaced">
        <div class="formRow__field formRow__field--noLabel">
          <div class="form-checkbox">
            <input id="offer-acceptTerms" class="form-checkbox__input" type="checkbox" v-model="acceptTerms" />
            <label class="filterItem__optionLabel form-checkbox__label" for="offer-acceptTerms">
              <span
                >{{ $t('forms.fields.acceptTermsPre') }} <a :href="termsUrl" target="_blank">{{ $t('forms.fields.terms') }}</a></span
              >
            </label>
            <p v-if="isError('acceptTerms')" class="form-fieldError form-fieldError--spaced">{{ $t('forms.errors.acceptTerms') }}</p>
          </div>
          <!-- .form-checkbox -->
        </div>
        <!-- .formRow__field -->
      </div>
      <!-- .formRow -->

      <div class="formRow formRow--spaced">
        <div class="formRow__field formRow__field--noLabel">
          <input
            type="submit"
            class="button button--primary button--arrow button--submit"
            :class="{ 'button--loading': loading }"
            :disabled="loading"
            :value="$t('forms.offer.submit')"
          />
        </div>
        <!-- .formRow__field -->
      </div>
      <!-- .formRow -->
    </form>
  </Modal>
</template>

<script>
import { WPSettings } from '@/WPSettings'
import axios from 'axios'
import Modal from '@/components/Modal'

export default {
  name: 'modalOffer',
  data() {
    return {
      loading: false,
      errors: [],
      errorMsg: '',
      successMsg: '',
      salutation: '',
      fname: '',
      lname: '',
      email: '',
      phoneNumber: '',
      isTradeCar: null,
      license: '',
      mileage: '',
      comments: '',
      acceptTerms: false,
      termsUrl: this.$store.state.settings.general.termsUrl,
    }
  },
  props: ['active', 'introText', 'vehicle'],
  components: {
    Modal,
  },
  computed: {
    currentVehicleId() {
      const currentVehicle = this.$store.getters['vehicles/current']
      return currentVehicle ? currentVehicle.id : null
    },
  },
  methods: {
    closeModal() {
      // Emit to parent component
      this.$emit('modalClosed', true)

      // Reset errors - just because it's prettier
      this.errors = []
    },

    isError(field) {
      // Check if field is in the error list
      return this.errors.includes(field)
    },

    validateErrorFields() {
      // Re-check the error fields
      if (this.errors.length > 0) {
        this.errors.forEach((error) => {
          if (this[error]) {
            // Field has a value, so remove it from the error list
            this.errors = this.errors.filter((el) => el !== error)
          }
        })
      }
    },

    validateForm() {
      // Reset errors
      this.errors = []

      // Fields to check
      const checkIfHasValue = ['salutation', 'fname', 'lname', 'email', 'phoneNumber', 'acceptTerms']

      // Check fields
      checkIfHasValue.forEach((field) => {
        if (!this[field]) {
          // Field has no value, so add it to error list
          this.errors.push(field)
        }
      })

      // Check tradeCar fields
      if (this.isTradeCar === null) {
        this.errors.push('isTradeCar')
      } else if (this.isTradeCar === true) {
        // Check license field
        if (!this.license) {
          this.errors.push('license')
        }
        // Check mileage field
        if (!this.mileage) {
          this.errors.push('mileage')
        }
      }
    },

    submitForm(e) {
      // Prevent form from being submitted by browser
      e.preventDefault()

      // Validate form
      this.validateForm()

      // If there are any errors, stop here
      if (this.errors.length > 0) {
        return false
      }

      // Do the call to WP REST
      this.loading = true

      axios
        .post(
          WPSettings.API_URL + '/form-offer',
          {
            vehicleId: this.currentVehicleId,
            salutation: this.salutation,
            fname: this.fname,
            lname: this.lname,
            email: this.email,
            phoneNumber: this.phoneNumber,
            isTradeCar: this.isTradeCar,
            license: this.license,
            mileage: this.mileage,
            comments: this.comments,
          },
          {
            headers:
              process.env.NODE_ENV === 'production'
                ? {
                    'X-WP-Nonce': WPSettings.NONCE,
                  }
                : {},
          }
        )
        .then((response) => {
          if (response.data.status === 'success') {
            // Update success message
            this.errorMsg = ''
            this.successMsg = response.data.message

            // Reset form
            this.salutation =
              this.fname =
              this.lname =
              this.email =
              this.phoneNumber =
              this.license =
              this.mileage =
              this.comments =
              this.acceptTerms =
                ''
            this.isTradeCar = null
          }
        })
        .catch((error) => {
          if (error.response.data.status === 'error') {
            // Update error message
            this.successMsg = ''
            this.errorMsg = error.response.data.message
          } else {
            this.successMsg = ''
            this.errorMsg = this.$t('forms.errors.somethingWentWrong')
          }
        })
        .finally(() => (this.loading = false))
    },
  },
}
</script>
